/* ─────────────────────────────────────────────────────────────────────────────
   SoFlo — shared "Add App" controller (site-wide).

   The installable thing is the Expo app at /apps/soflo-delivery/app/ (declared by
   assets/sf-pwa-manifest.json). layout/theme.liquid captures `beforeinstallprompt`
   into window.__sfInstall in <head>, because the event fires early and the browser
   only honours .prompt() on that exact object.

   Why this file exists: that captured event is a ONE-SHOT SINGLETON. There is one
   prompt per page, so several Add App buttons (footer + upsell trio + banner +
   homepage section) cannot each own a copy of the install logic — they have to
   share the single event. This controller owns it and drives every app CTA on the
   page.

   Contract — markup opts in with data attributes:
     [data-sfa-add]     a button that should trigger the real one-tap install
     [data-sfa-cta]     a wrapper to hide once the app is installed / we are in it
     [data-sfa-banner]  the dismissible bottom install banner
     [data-sfa-close]   dismiss control inside the banner

   A button NEVER dead-ends: where a programmatic install is impossible (iOS Safari
   has no such API; Firefox never fires the event) the click falls through to
   /pages/get-the-app, which carries the real per-platform instructions.
   ───────────────────────────────────────────────────────────────────────────── */
(function () {
  var GUIDE = '/pages/get-the-app';
  var DISMISS_KEY = 'sfaBannerDismissed';
  var DISMISS_DAYS = 14;

  var ua = navigator.userAgent || '';
  var isIOS = /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS reports as Mac
  var isAndroid = /android/i.test(ua);

  // Already running as the installed app? Then every "get the app" pitch is noise.
  var standalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    navigator.standalone === true;

  var SF = window.__sfInstall || { ready: false, installed: false, evt: null };

  function all(sel) {
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  }

  function hideAllCtas() {
    all('[data-sfa-cta]').forEach(function (el) { el.hidden = true; });
    hideBanner();
  }

  /* ── the bottom install banner ─────────────────────────────────────────────
     Lifts the site-wide "Track your order" fab (bottom-left) out from under
     itself via html[data-sfa-banner-open] — see sf-app.css.                   */
  function banner() { return document.querySelector('[data-sfa-banner]'); }

  function showBanner() {
    var b = banner();
    if (!b) return;
    b.hidden = false;
    // next frame, so the transition actually runs
    requestAnimationFrame(function () {
      b.setAttribute('data-open', '1');
      document.documentElement.setAttribute('data-sfa-banner-open', '1');
    });
  }

  function hideBanner() {
    var b = banner();
    if (!b) return;
    b.removeAttribute('data-open');
    document.documentElement.removeAttribute('data-sfa-banner-open');
    setTimeout(function () { b.hidden = true; }, 260);
  }

  function dismissedRecently() {
    try {
      var at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (!at) return false;
      return (Date.now() - at) < DISMISS_DAYS * 864e5;
    } catch (e) { return false; }
  }

  function rememberDismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
  }

  /* ── install ───────────────────────────────────────────────────────────────
     Success is driven by the real `appinstalled` event, never by the click:
     if the user dismisses the browser sheet nothing was installed and we must
     not claim otherwise.                                                       */
  function install(btn) {
    var evt = (window.__sfInstall || {}).evt;

    // No prompt available (iOS Safari, Firefox, or the event has not fired yet)
    // -> send them to the page that explains how, per platform.
    if (!evt) { window.location.href = GUIDE; return; }

    btn.disabled = true;
    evt.prompt();
    evt.userChoice.then(function (choice) {
      window.__sfInstall.evt = null;
      window.__sfInstall.ready = false;
      btn.disabled = false;
      if (!choice || choice.outcome !== 'accepted') {
        // Prompt is spent. Any further click must go to the manual instructions.
        hideBanner();
      }
      // accepted -> the sf-installed listener below clears the CTAs
    });
  }

  function ready() {
    if (standalone) { hideAllCtas(); return; }

    all('[data-sfa-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        install(btn);
      });
    });

    var b = banner();
    if (b) {
      all('[data-sfa-close]').forEach(function (c) {
        c.addEventListener('click', function () { rememberDismiss(); hideBanner(); });
      });

      // Only pitch the banner on a phone, and only if they have not waved it off.
      // Desktop already has the footer + trio + homepage section.
      var phone = (isIOS || isAndroid) && window.matchMedia('(max-width: 900px)').matches;
      if (phone && !dismissedRecently() && !SF.installed) {
        setTimeout(showBanner, 2200); // let them look at the page first
      }
    }
  }

  window.addEventListener('sf-installed', function () {
    standalone = true;
    hideAllCtas();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
